import { PageHeader } from "@finitless/shared/components";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Button, CustomGoogleAutocompleteInput, Icon } from "../../../ui";
import Breadcrumb from "../../../ui/Navigation/Breadcrumb";
import { getTimezoneFromLocation } from "@finitless/shared/lib";
import "../../../catalog-ordering.css";

const BusinessAddressSelection = () => {
  const { t } = useTranslation("stores");
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [googleMapsError] = useState(false);

  // Google Maps API key
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  const handleAddressChange = async (addressData) => {
    if (!addressData?.map_data?.place_id) {
      setError(t("addressSelection.validation.selectValidPlace"));
      setSelectedPlace(null);
      return;
    }

    const timezone = await getTimezoneFromLocation(
      addressData.location.lat,
      addressData.location.lng,
      GOOGLE_MAPS_API_KEY,
    );

    setSelectedPlace({
      place_id: addressData.map_data.place_id,
      name: addressData.name || addressData.address,
      address: addressData.address,
      location: addressData.location,
      phone: addressData.phone,
      price_level: addressData.price_level,
      rating: addressData.rating,
      user_ratings_total: addressData.user_ratings_total,
      website: addressData.website,
      url: addressData.url,
      opening_hours: addressData.opening_hours,
      timezone: timezone,
      address_components: {
        street_number: addressData.street_number,
        neighborhood: addressData.neighborhood,
        route: addressData.route,
        city_name: addressData.city_name,
        locality: addressData.locality,
        sublocality: addressData.sublocality,
        country: addressData.country,
        country_code: addressData.country_code,
        state: addressData.state,
        state_code: addressData.state_code,
        zipcode: addressData.zipcode,
      },
    });

    setError("");
  };

  const handleContinue = () => {
    if (!selectedPlace) {
      setError(t("addressSelection.validation.selectValidAddress"));
      return;
    }

    // Create a completely serializable object with only primitive values
    const serializablePlaceData = {
      place_id: selectedPlace.place_id,
      name: selectedPlace.name,
      address: selectedPlace.address,
      location: selectedPlace.location,
      phone: selectedPlace.phone,
      price_level: selectedPlace.price_level,
      rating: selectedPlace.rating,
      user_ratings_total: selectedPlace.user_ratings_total,
      website: selectedPlace.website,
      url: selectedPlace.url,
      timezone: selectedPlace.timezone,
      address_components: selectedPlace.address_components,
      // Convert opening_hours to serializable format (remove any functions)
      opening_hours: selectedPlace.opening_hours
        ? {
            open_now: selectedPlace.opening_hours.open_now,
            periods: selectedPlace.opening_hours.periods
              ? selectedPlace.opening_hours.periods.map((period) => ({
                  close: period.close
                    ? {
                        day: period.close.day,
                        time: period.close.time,
                      }
                    : null,
                  open: period.open
                    ? {
                        day: period.open.day,
                        time: period.open.time,
                      }
                    : null,
                }))
              : [],
            weekday_text: selectedPlace.opening_hours.weekday_text || [],
          }
        : null,
    };

    // Navigate to business creation form with place data
    navigate("/menu/stores/create/form", {
      state: {
        placeData: serializablePlaceData,
      },
    });
  };

  const handleSkip = () => {
    // Navigate to business creation form without place data
    navigate("/menu/stores/create/form");
  };

  const handleBack = () => {
    navigate("/menu/stores");
  };

  const breadcrumbItems = [
    {
      label: t("addressSelection.breadcrumb.stores"),
      path: "/menu/stores",
      icon: "Store",
    },
    {
      label: t("addressSelection.breadcrumb.createStore"),
      path: "/menu/stores/create",
      disabled: true,
    },
    {
      label: t("addressSelection.breadcrumb.selectAddress"),
      path: null,
      active: true,
    },
  ];

  return (
    <div
      className="ordering-catalog-surface space-y-4 sm:space-y-6"
      data-ordering-expression="product"
    >
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Main Content */}
      <div className="border-border bg-background rounded-lg border">
        {/* Header */}
        <div className="border-border border-b p-4 sm:p-6">
          <PageHeader
            title={t("addressSelection.header.title")}
            subtitle={t("addressSelection.header.subtitle")}
          />
        </div>

        {/* Form Content */}
        <div className="p-4 sm:p-6">
          <div className="space-y-4 sm:space-y-6">
            {/* Address Input */}
            <div>
              <label className="text-foreground mb-2 block text-sm font-medium">
                {t("addressSelection.fields.storeName.label")}
                <span className="text-destructive ml-1">*</span>
              </label>
              <div className="relative">
                {googleMapsError ? (
                  <div className="border-destructive bg-secondary text-destructive flex h-10 w-full items-center rounded-lg border px-3 py-2 text-sm sm:h-12 sm:px-4 sm:py-3 sm:text-base">
                    <Icon
                      name="AlertCircle"
                      className="mr-2 h-4 w-4 sm:h-5 sm:w-5"
                    />
                    {t("addressSelection.states.googleMapsUnavailable")}
                  </div>
                ) : (
                  <>
                    <CustomGoogleAutocompleteInput
                      apiKey={GOOGLE_MAPS_API_KEY}
                      onChangeAddress={handleAddressChange}
                      placeholder={t(
                        "addressSelection.fields.storeName.placeholder",
                      )}
                      types={[
                        "restaurant",
                        "bar",
                        "cafe",
                        "bakery",
                        "meal_delivery",
                      ]}
                      fields={[
                        "place_id",
                        "name",
                        "formatted_address",
                        "geometry",
                        "address_components",
                        "international_phone_number",
                        "opening_hours",
                        "price_level",
                        "rating",
                        "user_ratings_total",
                        "website",
                        "url",
                      ]}
                      countryCode="*"
                      className="input-autocomplete border-border bg-secondary focus:ring-ring h-10 w-full rounded-lg border px-3 py-2 text-sm text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:outline-none sm:h-12 sm:px-4 sm:py-3 sm:text-base"
                    />
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <Icon
                        name="MapPin"
                        className="text-muted-foreground h-4 w-4 sm:h-5 sm:w-5"
                      />
                    </div>
                  </>
                )}
              </div>
              <p className="text-muted-foreground mt-2 text-xs sm:text-sm">
                {t("addressSelection.fields.storeName.helper")}
              </p>
              {error && (
                <p className="text-destructive mt-2 text-xs sm:text-sm">
                  {error}
                </p>
              )}
            </div>

            {/* Selected Place Info */}
            {selectedPlace && (
              <div className="border-border bg-secondary rounded-lg border p-3 sm:p-4">
                <h3 className="mb-3 text-base font-medium text-white sm:text-lg">
                  {t("addressSelection.selectedPlace.title")}
                </h3>
                <div className="space-y-2 sm:space-y-3">
                  <div>
                    <p className="text-foreground text-xs sm:text-sm">
                      <span className="font-medium">
                        {t("addressSelection.selectedPlace.name")}
                      </span>
                      <span className="ml-2 break-words">
                        {selectedPlace.name}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-foreground text-xs sm:text-sm">
                      <span className="font-medium">
                        {t("addressSelection.selectedPlace.address")}
                      </span>
                      <span className="ml-2 break-words">
                        {selectedPlace.address}
                      </span>
                    </p>
                  </div>
                  {selectedPlace.phone && (
                    <div>
                      <p className="text-foreground text-xs sm:text-sm">
                        <span className="font-medium">
                          {t("addressSelection.selectedPlace.phone")}
                        </span>
                        <span className="ml-2">{selectedPlace.phone}</span>
                      </p>
                    </div>
                  )}

                  {selectedPlace.rating && (
                    <div>
                      <p className="text-foreground text-xs sm:text-sm">
                        <span className="font-medium">
                          {t("addressSelection.selectedPlace.rating")}
                        </span>
                        <span className="ml-2">{selectedPlace.rating}/5</span>
                        {selectedPlace.user_ratings_total && (
                          <span className="text-muted-foreground ml-1">
                            ({selectedPlace.user_ratings_total}{" "}
                            {t("addressSelection.selectedPlace.reviews")})
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  {selectedPlace.price_level && (
                    <div>
                      <p className="text-foreground text-xs sm:text-sm">
                        <span className="font-medium">
                          {t("addressSelection.selectedPlace.priceLevel")}
                        </span>
                        <span className="ml-2">
                          {"$".repeat(selectedPlace.price_level)}
                        </span>
                      </p>
                    </div>
                  )}

                  {selectedPlace.website && (
                    <div>
                      <p className="text-foreground text-xs sm:text-sm">
                        <span className="font-medium">
                          {t("addressSelection.selectedPlace.website")}
                        </span>
                        <a
                          href={selectedPlace.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary ml-2 break-all"
                        >
                          {selectedPlace.website.length > 50
                            ? `${selectedPlace.website.substring(0, 50)}...`
                            : selectedPlace.website}
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="border-border flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-0 sm:pt-6">
              <Button
                onClick={handleBack}
                variant="outline"
                size="lg"
                className="border-border text-foreground hover:bg-secondary w-full sm:w-auto"
              >
                <Icon name="ArrowLeft" className="mr-2 h-4 w-4" />
                {t("common:actions.back")}
              </Button>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  className="text-muted-foreground text-center text-sm transition-colors hover:text-white sm:text-left sm:text-base"
                >
                  {t("addressSelection.actions.skipManual")}
                </Button>
                <Button
                  variant="default"
                  onClick={handleContinue}
                  disabled={!selectedPlace}
                  size="lg"
                  className="w-full disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                >
                  {t("addressSelection.actions.continue")}
                  <Icon name="ArrowRight" className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Info Box */}
            <div className="border-primary bg-primary/20 mt-4 rounded-lg border p-3 sm:mt-6 sm:p-4">
              <div className="flex items-start">
                <Icon
                  name="Info"
                  className="text-primary mt-0.5 mr-2 h-4 w-4 flex-shrink-0 sm:mr-3 sm:h-5 sm:w-5"
                />
                <div>
                  <h3 className="text-primary mb-2 text-sm font-medium sm:text-base">
                    {t("addressSelection.help.title")}
                  </h3>
                  <p className="text-primary text-xs sm:text-sm">
                    {t("addressSelection.help.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessAddressSelection;
